import { critterFromTx, type Critter } from "../creatures/dna";
import { drawCritter } from "../creatures/draw";
import {
  bodyIndexForCreature,
  resolveKnownProgram,
} from "../data/programs";
import type { BlockSummary, HarborEvent, MempoolTx } from "../data/types";
import { snap } from "../sprites/blit";
import { critterHitBox, critterLabelAnchor } from "../sprites/creatures";
import {
  dragonColorFromHash,
  dragonHeightLabel,
  dragonLabelAnchor,
  dragonLabelText,
  dragonSeat,
  drawDragonSprite,
  formatHeight,
  formatLatestHeight,
  type SeaDragon,
} from "../sprites/dragons";
import { drawTropicalShore, shoreLayout } from "../sprites/scene";
import {
  detailFromCritter,
  detailFromDragon,
  type EntityDetail,
} from "../ui/detail";
import { shortTxLabel } from "../util/base58";
import { isValidTxId, normalizeTxId } from "../util/txid";
import { TheaterAudio } from "./audio";
import { LabelOverlay, type OverlayLabel } from "./labels";

export interface TheaterHud {
  setPending(n: number): void;
  setHeight(h: number | string): void;
  setStream(on: boolean): void;
  toast(text: string): void;
  /** Throttled textual event line for screen readers / status region. */
  announce?(text: string): void;
}

export type TheaterSelectHandler = (detail: EntityDetail) => void;

type HitRegion = {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Label / hit id — `c:<txid>` or `d:<hash>`. */
  id: string;
};

type BeachSlot = { x: number; y: number; freeAt: number; index: number };

/** Keep the shore readable — hard caps on concurrent actors. */
const MAX_WILD = 8;
/** Overlapping trail OK; prefer clarity over a crowded dock. */
const MAX_DRAGONS = 4;
/** Empty-dock dwell before swim-away (boarding optional). */
const DOCK_STAY_MS = 4_500;
/** Max time to finish a climb once a rider is assigned. */
const BOARDING_MAX_MS = 8_000;
/** Stagger concurrent dock slots so dragons don't stack. */
const DOCK_STEP_X = 20;

export class Theater {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private hud: TheaterHud;
  private network: "mainnet" | "testnet";
  private critters = new Map<string, Critter>();
  private dragons: SeaDragon[] = [];
  /** Recent tip metadata from quiet block events — used when a tx summons a dragon. */
  private recentBlocks = new Map<number, BlockSummary>();
  private rideAssignments = new Map<string, string>();
  private beachSlots: BeachSlot[] = [];
  private critterSlot = new Map<string, number>();
  private hits: HitRegion[] = [];
  private labels: LabelOverlay;
  private width = 0;
  private height = 0;
  /** CSS upscale of the low-res buffer — keep ≤2 so 64px sprites stay crisp. */
  private pixelScale = 2;
  private t = 0;
  private quiet = true;
  private audio = new TheaterAudio();
  private paused = false;
  private reducedMotion = false;
  private onSelect: TheaterSelectHandler | null = null;
  /** Label id → entity for click / keyboard activation. */
  private labelTargets = new Map<string, EntityDetail>();

  constructor(
    canvas: HTMLCanvasElement,
    hud: TheaterHud,
    network: "mainnet" | "testnet" = "testnet",
  ) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d unavailable");
    this.ctx = ctx;
    this.hud = hud;
    this.network = network;

    let layer = document.getElementById("label-layer");
    if (!layer) {
      layer = document.createElement("div");
      layer.id = "label-layer";
      const stage = document.getElementById("stage") ?? canvas.parentElement;
      (stage ?? document.body).appendChild(layer);
    }
    this.labels = new LabelOverlay(layer);
    this.labels.setActivateHandler((id) => this.activateLabel(id));

    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window
      .matchMedia("(prefers-reduced-motion: reduce)")
      .addEventListener("change", (e) => {
        this.reducedMotion = e.matches;
      });

    this.resize();
    window.addEventListener("resize", () => this.resize());
    this.canvas.addEventListener("click", (e) => this.onClick(e));
    this.canvas.addEventListener("mousemove", (e) => this.onMove(e));
  }

  setMuted(m: boolean): void {
    this.audio.setMuted(m);
  }

  setSelectHandler(fn: TheaterSelectHandler | null): void {
    this.onSelect = fn;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  setPaused(p: boolean): void {
    this.paused = p;
  }

  togglePaused(): boolean {
    this.paused = !this.paused;
    return this.paused;
  }

  get prefersReducedMotion(): boolean {
    return this.reducedMotion;
  }

  /** Debug / tests — live wild + catching count. */
  get actorCounts(): { wild: number; total: number; dragons: number } {
    return {
      wild: this.wildCount(),
      total: this.critters.size,
      dragons: this.dragons.length,
    };
  }

  handle(event: HarborEvent): void {
    switch (event.type) {
      case "snapshot":
        this.syncMempool(event.data.mempool);
        if (event.data.latestBlock) {
          const tip = formatHeight(event.data.latestBlock.height);
          if (tip) this.hud.setHeight(tip);
        }
        this.hud.setStream(event.data.connected);
        break;
      case "mempool":
        this.syncMempool(event.data.mempool);
        break;
      case "block":
        this.onBlock(event.data);
        break;
      case "transaction": {
        const txid = normalizeTxId(event.data.txid);
        if (!txid) {
          console.warn(
            "[theater] skip transaction: invalid txid",
            String(event.data.txid ?? "").slice(0, 24),
          );
          break;
        }
        const height =
          event.data.height != null && event.data.height > 0
            ? event.data.height
            : undefined;
        const programIds = Array.isArray(event.data.program_ids)
          ? event.data.program_ids.map(String).filter(Boolean)
          : [];
        this.onTransaction(
          txid,
          event.data.success !== false,
          height,
          programIds,
        );
        break;
      }
      case "status":
        this.hud.setStream(event.data.connected);
        break;
    }
  }

  private wildCount(): number {
    return [...this.critters.values()].filter((c) => c.phase === "wild").length;
  }

  /**
   * Mempool snapshots must not invent shore critters — only live WS
   * `transaction` events spawn. Do not flee on snapshot miss either:
   * recent-mempool REST often omits live WS txs and would clear the shore.
   */
  private syncMempool(_txs: MempoolTx[]): void {
    const now = performance.now();
    for (const [id, c] of this.critters) {
      if (
        (c.phase === "fled" || c.phase === "caught") &&
        now - c.phaseChangedAt > 4_000
      ) {
        this.critters.delete(id);
        this.critterSlot.delete(id);
      }
    }
    this.refreshPending();
  }

  private beachBounds() {
    const layout = shoreLayout(this.width, this.height);
    return {
      shoreLeft: Math.max(18, layout.shoreLeft),
      shoreRight: Math.max(40, layout.shoreRight),
      sandTop: layout.sandTop,
      sandBottom: layout.sandBottom,
    };
  }

  /** Front dragon boarding dock — just into the surf past the sand edge. */
  private dockX(): number {
    const shoreEdge = Math.floor(this.width * 0.52);
    return shoreEdge + 10;
  }

  private ensureBeachSlots(): void {
    if (this.beachSlots.length > 0) return;
    const b = this.beachBounds();
    // Spread across the sand; leave a clear strip by the water for boarding.
    const cols = 4;
    const rows = 2;
    const left = b.shoreLeft + 14;
    const right = Math.max(left + 48, b.shoreRight - 22);
    const top = b.sandTop + 10;
    const bottom = Math.max(top + 18, b.sandBottom - 12);
    let index = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = left + col * ((right - left) / Math.max(1, cols - 1));
        const y = top + row * ((bottom - top) / Math.max(1, rows - 1));
        this.beachSlots.push({
          x: snap(x),
          y: snap(y),
          freeAt: 0,
          index: index++,
        });
      }
    }
  }

  private placeEntering(c: Critter): void {
    this.ensureBeachSlots();
    const now = performance.now();
    const slot = [...this.beachSlots].sort((a, b) => a.freeAt - b.freeAt)[0]!;
    slot.freeAt = now + 8000 + Math.random() * 4000;
    // Tiny jitter only — keep assigned lanes from collapsing into one pile.
    c.homeX = snap(slot.x + ((slot.index % 2) - 0.5) * 2);
    c.homeY = snap(slot.y + (slot.index % 3) - 1);
    c.x = snap(c.homeX - 20);
    c.y = snap(c.homeY);
    c.phaseChangedAt = now;
    this.critterSlot.set(c.id, slot.index);
  }

  /**
   * Quiet tip updates only — never spawn an empty sea dragon from a bare block.
   * Dragons appear when a live WS transaction references the height.
   */
  private onBlock(block: BlockSummary): void {
    if (block.height > 0) {
      this.recentBlocks.set(block.height, block);
      if (this.recentBlocks.size > 64) {
        const oldest = this.recentBlocks.keys().next().value;
        if (oldest != null) this.recentBlocks.delete(oldest);
      }
    }
    const heights =
      block.heights?.length
        ? block.heights
        : block.height > 0
          ? [block.height]
          : [];
    // Tip HUD still advances on empty blocks; theater dragons do not.
    // Always a single latest tip — never a coalesced list that resizes the HUD.
    const tip = formatLatestHeight(heights) || formatHeight(block.height);
    if (tip) this.hud.setHeight(tip);
  }

  private onTransaction(
    txid: string,
    success: boolean,
    height?: number,
    programIds: string[] = [],
  ): void {
    // Defense in depth — only real Arch tx digests become shore creatures.
    if (!isValidTxId(txid) || !shortTxLabel(txid)) {
      console.warn("[theater] skip spawn: invalid txid", txid.slice(0, 24));
      return;
    }
    // Sea dragons only for successful txs that name a block tip.
    // Also covers height upgrades on a second WS event for an existing critter.
    if (success && height != null && height > 0) {
      this.ensureDragonForHeight(height);
    }
    let c = this.critters.get(txid);
    if (!c) {
      if (success && this.wildCount() < MAX_WILD) {
        try {
          c = critterFromTx({
            txid,
            blockHeight: height,
            programIds,
            success,
          });
        } catch (err) {
          console.warn("[theater] skip spawn:", err);
          return;
        }
        this.placeEntering(c);
        this.critters.set(txid, c);
        const appear = `A wild ${c.name} appeared!`;
        this.hud.toast(appear);
        this.hud.announce?.(appear);
        this.blip(760);
      } else {
        return;
      }
    } else {
      if (height != null && height > 0 && !c.blockHeight) {
        c.blockHeight = height;
      }
      if (programIds.length && c.programIds.length === 0) {
        c.programIds = programIds;
        const known = resolveKnownProgram(programIds);
        if (known) {
          const body = bodyIndexForCreature(known.body);
          if (body != null) c.body = body;
          c.programLabel = known.label;
        }
      }
      c.result = success ? "success" : "failed";
    }
    if (!success) {
      c.phase = "fled";
      c.phaseChangedAt = performance.now();
      this.refreshPending();
      return;
    }
    this.refreshPending();
  }

  private dragonHeights(d: SeaDragon): number[] {
    if (d.heights?.length) return d.heights;
    return d.height > 0 ? [d.height] : [];
  }

  /** Unboarded wilds still waiting for a dock that covers `height`. */
  private wildsWaitingForHeight(height: number): Critter[] {
    if (!(height > 0)) return [];
    return [...this.critters.values()].filter(
      (c) =>
        c.phase === "wild" &&
        c.blockHeight === height &&
        !this.rideAssignments.has(c.id),
    );
  }

  private hasWildWaitingForHeight(height: number): boolean {
    return this.wildsWaitingForHeight(height).length > 0;
  }

  /** True when this dock still has height-matched wilds that need it. */
  private dockNeededByHeightMatchedWilds(d: SeaDragon): boolean {
    return this.dragonHeights(d).some((h) => this.hasWildWaitingForHeight(h));
  }

  /**
   * After a dragon leaves (empty or with one rider), respawn a covering dock
   * for any leftover same-height wilds. Never spawn for heights with zero waiters.
   */
  private respawnCoveringForDeparted(departed: SeaDragon): void {
    for (const h of this.dragonHeights(departed)) {
      if (this.hasWildWaitingForHeight(h)) {
        this.ensureDragonForHeight(h);
      }
    }
  }

  private foldHeightOntoDragon(d: SeaDragon, height: number): void {
    const heights = this.dragonHeights(d);
    if (!heights.includes(height)) {
      heights.push(height);
      heights.sort((a, b) => a - b);
      d.heights = heights;
    }
    d.height = height;
    const tip = formatLatestHeight(heights);
    if (tip) this.hud.setHeight(tip);
  }

  /** Spawn (or reuse) a dragon for a height that has at least one live tx. */
  private ensureDragonForHeight(height: number): void {
    if (!(height > 0)) return;
    const covers = (d: SeaDragon) =>
      d.height === height || !!d.heights?.includes(height);
    if (this.activeDragons().some(covers)) return;

    const active = this.activeDragons();
    if (active.length >= MAX_DRAGONS) {
      // Fold only onto a waiting empty dragon that can still board someone.
      const foldTarget = [...active]
        .filter((d) => d.phase === "waiting" && !d.riderId)
        .sort((a, b) => b.born - a.born)[0];
      if (foldTarget) {
        this.foldHeightOntoDragon(foldTarget, height);
        return;
      }
      // Otherwise retarget an incoming dragon (not yet boarding) rather than
      // attaching the height to a busy rider that will swim away without them.
      const incoming = active.find((d) => d.phase === "incoming");
      if (incoming) {
        const cached = this.recentBlocks.get(height);
        incoming.height = height;
        incoming.heights = undefined;
        incoming.hash = cached?.hash ?? `height:${height}`;
        incoming.color = dragonColorFromHash(incoming.hash);
        const tip = formatHeight(height);
        if (tip) this.hud.setHeight(tip);
        return;
      }
      // All docks busy boarding — leave room; respawn after one departs.
      return;
    }

    const cached = this.recentBlocks.get(height);
    const block: BlockSummary = cached ?? {
      hash: `height:${height}`,
      height,
      timestamp: new Date().toISOString(),
      transaction_count: 1,
    };
    this.pushDragon(block);
    const label = formatHeight(height) || `#${height}`;
    const msg = `Block ${label} summoned a sea dragon`;
    this.hud.toast(msg);
    this.hud.announce?.(msg);
    this.blip(220);
  }

  private refreshPending(): void {
    const pending = [...this.critters.values()].filter(
      (c) => c.phase === "wild" || c.phase === "catching",
    ).length;
    this.hud.setPending(pending);
    this.quiet = pending === 0;
  }

  private activeDragons(): SeaDragon[] {
    return this.dragons
      .filter((d) => d.phase !== "departing")
      .sort((a, b) => a.born - b.born);
  }

  private pushDragon(block: BlockSummary): void {
    const heights =
      block.heights?.length
        ? [...block.heights]
        : block.height > 0
          ? [block.height]
          : [];
    // HUD tip may also advance when a non-empty tip's first tx arrives.
    const tip = formatLatestHeight(heights) || formatHeight(block.height);
    if (tip) this.hud.setHeight(tip);
    const beach = this.beachBounds();
    this.dragons.push({
      height: block.height,
      heights: heights.length > 1 ? heights : undefined,
      hash: block.hash,
      born: performance.now(),
      dockedAt: 0,
      x: this.width + 50,
      y: beach.sandTop + 12,
      phase: "incoming",
      color: dragonColorFromHash(block.hash),
      riderId: null,
      passengers: 0,
    });
  }

  /** Roll in → hold at dock → leave after stay timer. Boarding is optional. */
  private advanceDragons(now: number): void {
    const dock = this.dockX();
    const baseY = this.beachBounds().sandTop + 14;
    const active = this.activeDragons();

    for (let i = 0; i < active.length; i++) {
      const d = active[i]!;
      const targetX = dock + i * DOCK_STEP_X;
      const targetY = baseY + (active.length - 1 - i) * 3;

      if (d.phase === "incoming") {
        // Step toward dock — pure lerp+snap can stall a few px short forever.
        const dx = targetX - d.x;
        const dy = targetY - d.y;
        if (Math.abs(dx) > 0) {
          d.x = snap(d.x + Math.sign(dx) * Math.min(2.8, Math.abs(dx)));
        }
        if (Math.abs(dy) > 0) {
          d.y = snap(d.y + Math.sign(dy) * Math.min(1.5, Math.abs(dy)));
        }
        if (Math.abs(d.x - targetX) <= 2 && Math.abs(d.y - targetY) <= 2) {
          d.x = targetX;
          d.y = targetY;
          d.phase = "waiting";
          d.dockedAt = now;
        }
      } else if (d.phase === "waiting") {
        d.x = snap(targetX);
        d.y = snap(
          d.y + Math.sign(targetY - d.y) * Math.min(1, Math.abs(targetY - d.y)),
        );
        // Empty dragons leave after a dwell — but hold the dock while
        // same-height wilds are still waiting to board (prevents stranding).
        if (d.dockedAt > 0 && !d.riderId && now - d.dockedAt >= DOCK_STAY_MS) {
          if (this.dockNeededByHeightMatchedWilds(d)) {
            d.dockedAt = now;
          } else {
            this.beginDepart(d, now);
          }
        }
      } else if (d.phase === "boarding") {
        d.x = snap(dock);
        d.y = snap(
          d.y + Math.sign(targetY - d.y) * Math.min(1, Math.abs(targetY - d.y)),
        );
        const rider = d.riderId ? this.critters.get(d.riderId) : undefined;
        const boarded = !!rider && rider.catchProgress >= 1;
        const boardedSince = rider?.phaseChangedAt ?? now;
        // Depart once the rider is seated, or abandon a stuck climb.
        if (boarded) {
          this.beginDepart(d, now);
        } else if (now - boardedSince >= BOARDING_MAX_MS) {
          this.beginDepart(d, now);
        }
      }
    }
  }

  private beginDepart(d: SeaDragon, now: number): void {
    if (d.phase === "departing") return;
    if (d.riderId) {
      const rider = this.critters.get(d.riderId);
      if (rider?.phase === "catching" && rider.catchProgress < 0.85) {
        // Climb never finished — release back to the beach.
        rider.phase = "wild";
        rider.phaseChangedAt = now;
        this.rideAssignments.delete(d.riderId);
        d.riderId = null;
        d.passengers = 0;
        this.audio.stopFootsteps();
      } else if (
        rider &&
        (rider.phase === "catching" || rider.phase === "caught")
      ) {
        d.passengers = 1;
        rider.catchProgress = 1;
      }
    }
    d.phase = "departing";
    // One rider per trip is fine — respawn a covering dock for leftovers.
    this.respawnCoveringForDeparted(d);
  }

  /**
   * Waiting dock dragon with no rider. Height-tagged creatures only board a
   * dragon that covers their tip; heightless creatures must not steal a dock
   * that height-matched waiters still need.
   */
  private openDockDragon(forHeight?: number): SeaDragon | undefined {
    const open = this.activeDragons().filter(
      (d) => d.phase === "waiting" && d.dockedAt > 0 && !d.riderId,
    );
    if (forHeight != null && forHeight > 0) {
      return open.find(
        (d) => d.height === forHeight || !!d.heights?.includes(forHeight),
      );
    }
    return open.find((d) => !this.dockNeededByHeightMatchedWilds(d));
  }

  start(): void {
    const loop = (now: number) => {
      if (!this.paused) {
        this.t = now / 1000;
        this.tick(now);
      }
      this.draw();
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  private tick(now: number): void {
    const beach = this.beachBounds();
    this.advanceDragons(now);

    for (const c of this.critters.values()) {
      if (!this.reducedMotion) {
        c.bob += 0.05 + c.size * 0.01;
      }

      if (c.phase === "wild") {
        const dx = c.homeX - c.x;
        const dy = c.homeY - c.y;
        const dist = Math.hypot(dx, dy);

        if (dist > 2) {
          c.x = snap(c.x + Math.sign(dx) * Math.min(1.5, Math.abs(dx)));
          c.y = snap(c.y + Math.sign(dy) * Math.min(1.5, Math.abs(dy)));
        } else if (this.reducedMotion) {
          c.x = snap(c.homeX);
          c.y = snap(c.homeY);
        } else {
          // Whole-pixel idle near the assigned slot — no subpixel sway pile-up.
          c.x = snap(c.homeX + Math.round(Math.sin(c.bob * 0.7 + c.size)));
          c.y = snap(c.homeY + Math.round(Math.cos(c.bob * 0.55)));
          c.x = Math.min(c.homeX + 2, Math.max(c.homeX - 2, c.x));
          c.y = Math.min(c.homeY + 2, Math.max(c.homeY - 2, c.y));
          c.x = Math.min(beach.shoreRight - 18, c.x);
        }

        // Settled on the beach long enough to finish the short walk-in.
        const distHome = Math.hypot(c.homeX - c.x, c.homeY - c.y);
        const settled = now - c.phaseChangedAt > 1200 && distHome < 10;
        const openDock = this.openDockDragon(c.blockHeight);
        if (openDock && settled && !this.rideAssignments.has(c.id)) {
          this.rideAssignments.set(c.id, openDock.hash);
          openDock.riderId = c.id;
          openDock.phase = "boarding";
          c.phase = "catching";
          c.phaseChangedAt = now;
          c.catchProgress = 0;
          const seat = dragonSeat(openDock);
          c.homeX = seat.x;
          c.homeY = seat.y;
          this.audio.startFootsteps();
        }
      }

      if (c.phase === "catching") {
        const hash = this.rideAssignments.get(c.id);
        const dragon = this.dragons.find((d) => d.hash === hash);
        if (!dragon) {
          c.phase = "wild";
          this.rideAssignments.delete(c.id);
          this.audio.stopFootsteps();
          continue;
        }

        if (dragon.phase === "departing") {
          const seat = dragonSeat(dragon);
          c.x = seat.x;
          c.y = seat.y;
          continue;
        }

        if (dragon.phase === "boarding" || dragon.phase === "waiting") {
          const seat = dragonSeat(dragon);
          const was = c.catchProgress;
          c.catchProgress = Math.min(1, c.catchProgress + 0.02);
          const sdx = seat.x - c.x;
          const sdy = seat.y - c.y;
          if (Math.abs(sdx) > 0) {
            c.x = snap(c.x + Math.sign(sdx) * Math.min(2, Math.abs(sdx)));
          }
          if (Math.abs(sdy) > 0) {
            c.y = snap(c.y + Math.sign(sdy) * Math.min(2, Math.abs(sdy)));
          }
          const seated =
            c.catchProgress >= 1 ||
            Math.hypot(seat.x - c.x, seat.y - c.y) <= 3;
          if (seated) {
            c.x = seat.x;
            c.y = seat.y;
            c.catchProgress = 1;
            dragon.passengers = 1;
            // Depart as soon as the rider is on the seat.
            if (was < 1) {
              this.audio.stopFootsteps();
              const blockLabel = dragonHeightLabel(dragon);
              const boarded = `${c.name} climbed aboard block ${blockLabel}`;
              this.hud.toast(boarded);
              this.hud.announce?.(boarded);
              this.blip(520);
            }
            if (dragon.phase === "boarding") {
              this.beginDepart(dragon, now);
            }
          }
        }
      }
    }

    for (const d of this.dragons) {
      if (d.phase !== "departing") continue;
      d.x = snap(d.x + 2.4);
      if (d.riderId) {
        const rider = this.critters.get(d.riderId);
        if (rider) {
          const seat = dragonSeat(d);
          rider.x = seat.x;
          rider.y = seat.y;
        }
      }
      if (d.x > this.width + 60 && d.riderId) {
        const rider = this.critters.get(d.riderId);
        if (rider) {
          rider.phase = "caught";
          rider.phaseChangedAt = now;
        }
        this.rideAssignments.delete(d.riderId);
      }
    }

    this.dragons = this.dragons.filter((d) => d.x < this.width + 80);
    this.refreshPending();
  }

  private draw(): void {
    const { ctx, width: w, height: h } = this;
    ctx.globalAlpha = 1;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, w, h);
    this.hits = [];

    drawTropicalShore(ctx, w, h, this.t);

    const riding = new Set(
      this.dragons
        .map((d) => d.riderId)
        .filter((id): id is string => !!id),
    );

    // Draw only active beach actors — fled leftovers used to pile up unlabeled.
    const ordered = [...this.critters.values()]
      .filter(
        (c) =>
          !riding.has(c.id) &&
          (c.phase === "wild" || c.phase === "catching"),
      )
      .sort((a, b) => a.y - b.y);

    for (const c of ordered) {
      drawCritter(ctx, c);
      if (c.phase === "wild") {
        const box = critterHitBox(c);
        this.hits.push({ ...box, id: `c:${c.id}` });
      }
    }

    const dragonsOrdered = [...this.dragons].sort((a, b) => a.y - b.y);
    const drawT = this.reducedMotion || this.paused ? 0 : this.t;
    for (const d of dragonsOrdered) {
      drawDragonSprite(ctx, d, drawT);
      if (d.height > 0 && d.height < 1_000_000_000) {
        const dw = 96;
        const dh = 64;
        this.hits.push({
          x: snap(d.x - dw / 2),
          y: snap(d.y - dh / 2),
          w: dw,
          h: dh,
          id: `d:${d.hash}`,
        });
      }
      if (d.riderId) {
        const c = this.critters.get(d.riderId);
        if (c && (c.phase === "catching" || c.phase === "caught")) {
          const seat = dragonSeat(d);
          if (d.phase === "departing") {
            drawCritter(ctx, c, { seatX: seat.x, seatY: seat.y });
          } else {
            drawCritter(ctx, c);
          }
        }
      }
    }

    this.syncOverlayLabels(ordered, dragonsOrdered);

    if (this.quiet) this.drawQuietBanner();
  }

  private syncOverlayLabels(
    wildOrdered: Critter[],
    dragons: SeaDragon[],
  ): void {
    const labels: OverlayLabel[] = [];
    this.labelTargets.clear();
    const drawT = this.reducedMotion || this.paused ? 0 : this.t;

    for (const c of wildOrdered) {
      if (c.phase !== "wild") continue;
      const text = shortTxLabel(c.ref.txid);
      if (!text) {
        // Never show a blank chip — drop orphaned / invalid creatures.
        this.critters.delete(c.id);
        this.critterSlot.delete(c.id);
        continue;
      }
      const anchor = critterLabelAnchor(c);
      const id = `c:${c.id}`;
      const detail = detailFromCritter(c, this.network);
      this.labelTargets.set(id, detail);
      labels.push({
        id,
        text,
        x: anchor.x,
        y: anchor.y,
        ariaLabel: `${c.name}, transaction ${text}, opens details`,
        lane: 0,
      });
    }

    for (const d of dragons) {
      if (d.phase === "departing" && d.x > this.width - 10) continue;
      const anchor = dragonLabelAnchor(d, drawT);
      const id = `d:${d.hash}`;
      const rider = d.riderId ? this.critters.get(d.riderId) : undefined;
      const detail = detailFromDragon(d, this.network, rider?.name ?? null);
      this.labelTargets.set(id, detail);
      const label = dragonLabelText(d);
      labels.push({
        id,
        text: label,
        x: anchor.x,
        y: anchor.y,
        ariaLabel: `Sea dragon for block ${label}, opens details`,
        lane: 0,
      });
    }

    this.labels.sync(labels);
  }

  private activateLabel(id: string): void {
    const detail = this.labelTargets.get(id) ?? this.detailForHitId(id);
    if (detail) this.onSelect?.(detail);
  }

  private detailForHitId(id: string): EntityDetail | null {
    if (id.startsWith("c:")) {
      const c = this.critters.get(id.slice(2));
      return c ? detailFromCritter(c, this.network) : null;
    }
    if (id.startsWith("d:")) {
      const hash = id.slice(2);
      const d = this.dragons.find((x) => x.hash === hash);
      if (!d) return null;
      const rider = d.riderId ? this.critters.get(d.riderId) : undefined;
      return detailFromDragon(d, this.network, rider?.name ?? null);
    }
    return null;
  }

  private canvasPoint(e: MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const w = rect.width || 1;
    const h = rect.height || 1;
    return {
      x: ((e.clientX - rect.left) / w) * this.width,
      y: ((e.clientY - rect.top) / h) * this.height,
    };
  }

  private hitAt(x: number, y: number): HitRegion | undefined {
    for (let i = this.hits.length - 1; i >= 0; i--) {
      const hit = this.hits[i]!;
      if (
        x >= hit.x &&
        x < hit.x + hit.w &&
        y >= hit.y &&
        y < hit.y + hit.h
      ) {
        return hit;
      }
    }
    return undefined;
  }

  private onClick(e: MouseEvent): void {
    const p = this.canvasPoint(e);
    const hit = this.hitAt(p.x, p.y);
    if (!hit) return;
    e.preventDefault();
    e.stopPropagation();
    this.activateLabel(hit.id);
  }

  private onMove(e: MouseEvent): void {
    const p = this.canvasPoint(e);
    this.canvas.style.cursor = this.hitAt(p.x, p.y) ? "pointer" : "crosshair";
  }

  private drawQuietBanner(): void {
    const { ctx, width: w, height: h } = this;
    const bannerWidth = Math.min(120, w - 8);
    const x = snap(w * 0.5 - bannerWidth * 0.5);
    const y = snap(h * 0.28);
    ctx.fillStyle = "#0c141c";
    ctx.fillRect(x, y, bannerWidth, 12);
    ctx.fillStyle = "#c8e6d2";
    ctx.fillRect(x, y, bannerWidth, 1);
    ctx.fillRect(x, y + 11, bannerWidth, 1);
    ctx.fillRect(x, y, 1, 12);
    ctx.fillRect(x + bannerWidth - 1, y, 1, 12);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = "#e7f2ea";
    ctx.font = "6px ui-monospace, SF Mono, Menlo, monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("shore is quiet", snap(w * 0.5), y + 6);
    ctx.restore();
  }

  private resize(): void {
    // Max 2× integer CSS scale (native sprites are 64/96 — was 3–4× on 16–32px).
    this.pixelScale = 2;
    this.width = Math.max(160, Math.floor(window.innerWidth / this.pixelScale));
    this.height = Math.max(90, Math.floor(window.innerHeight / this.pixelScale));
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.canvas.style.width = `${this.width * this.pixelScale}px`;
    this.canvas.style.height = `${this.height * this.pixelScale}px`;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.imageSmoothingEnabled = false;
    this.beachSlots = [];
    this.labels.setBufferSize(this.width, this.height);
  }

  private blip(freq: number): void {
    this.audio.blip(freq);
  }
}
